/* eslint-disable no-underscore-dangle */
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { amplitudeContextSelector } from '@/features/amplitude/store/amplitude';
import { getProviderProduct } from '@/features/game-context/platform-helpers';
import { gameContextSelector } from '@/features/game-context/store/game-context';
import { Product, Provider } from '@/features/game-context/types';
import { getErrorType } from '@/features/game-window-integration/helpers/error-mapping';
import { isPlaytechLiveGameStudio } from '@/features/game-window-integration/providers/playtech/helpers';
import {
  PlaytechLoggerMessages,
  PlaytechMessageUcip,
  PlaytechRecevingEvents,
  PlaytechSendingEvents,
  SendUcipMessage,
  UcipHandler,
  UcipReceiveLoadingProgressInfo,
  UcipReceiveSoundInfo,
  UcipReceiveSpinStatusInfo,
  UcipSwitchGameInfo,
} from '@/features/game-window-integration/providers/playtech/types';
import { setErrorCorrelationId } from '@/features/game-window-integration/store/game-window-helper-data';
import {
  ProviderLoggerActionNames,
  ProviderLoggerMessages,
} from '@/features/game-window-integration/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';
import { processPayload } from '@/utils/process-error-payload';

const casinoFeatures = [
  [
    'errordelegation',
    {
      delegateErrorTypes: [
        'GENERIC_ERROR',
        'AUTHENTICATION_ERROR',
        'CONNECTION_ERROR',
        'INSUFFICIENT_FUNDS',
        'RECOVERABLE_ERROR',
      ],
    },
  ],
  'sound',
  'switchgame',
  'loadingstatus',
  'balanceupdate',
  'balancerefresh',
  'gameround',
  'autoplay',
  'actions',
  'gameinstance',
  'gamechange',
] as const;

const pokerFeatures = [...casinoFeatures, 'closegame'] as const;

const usePlaytechHandlers = (provider: Provider) => {
  const [volumeLevel, setVolumeLevel] = useState<number>(100);
  const dispatch = useDispatch();
  const { gameStudio } = useSelector(gameContextSelector);
  const getGameProviderFrame = useCallback(
    () => document.querySelector<HTMLIFrameElement>('iframe')?.contentWindow,
    [],
  );

  const { gameSwitchEnabled } = useSelector(amplitudeContextSelector);

  const sendUcipMessage: SendUcipMessage = useCallback(
    (event) => {
      const frame = getGameProviderFrame();
      if (!frame) {
        Logger.rumError({
          name: PlaytechLoggerMessages.NO_HANDLER,
          useCase: DdEventCategory.ProviderIntegration,
          eventType: DdEventType.Error,
          context: {
            provider,
          },
        });
        return;
      }
      frame?.postMessage(JSON.stringify(event), { targetOrigin: '*' });
    },
    [getGameProviderFrame, provider],
  );

  const isPoker = getProviderProduct(provider) === Product.Poker;

  const handleIntialization = useCallback(
    (payload) => {
      const version = isPoker ? payload?.data?.version : '1.0.7';

      const response: PlaytechMessageUcip = {
        _type: PlaytechSendingEvents.INITIALIZATION_RESPONSE,
        features: isPoker ? [...pokerFeatures] : [...casinoFeatures],
        ...(version && { version }),
      };

      sendUcipMessage(response);
    },
    [sendUcipMessage, isPoker],
  );

  const handleGameReady: UcipHandler<null> = useCallback(() => {
    setTimeout(() => {
      WrapperProxy.setGameLoaded();
    }, 1000);
  }, []);

  const handleGameLoadStarted: UcipHandler<null> = useCallback(
    () => WrapperProxy.setGameLoadStarted(),
    [],
  );

  const handleGameLoadProgress: UcipHandler<UcipReceiveLoadingProgressInfo> =
    useCallback(
      (payload) => WrapperProxy.setGameLoadProgress(payload?.data?.progress),
      [],
    );

  const handleSound: UcipHandler<UcipReceiveSoundInfo> = useCallback(
    (payload) => {
      WrapperProxy.setAudio(!payload?.data?.mute);

      if (payload?.data?.level === 0) {
        WrapperProxy.setAudio(false);
        setVolumeLevel(payload.data.level);
      }
    },
    [],
  );

  const handleSpinInProgress: UcipHandler<UcipReceiveSpinStatusInfo> =
    useCallback(
      ({ data }) =>
        WrapperProxy.setSpinInProgress(
          data._type === PlaytechRecevingEvents.ROUND_START,
        ),
      [],
    );

  const handleSwitchGame: UcipHandler<UcipSwitchGameInfo> = useCallback(
    ({ data }) => {
      if (!data?.gameCode || isPlaytechLiveGameStudio(gameStudio)) {
        return;
      }
      Logger.rumAction({
        name: ProviderLoggerActionNames.GAME_SWITCH_BROKEN_FLOW,
        useCase: DdEventCategory.ProviderIntegration,
        description: ProviderLoggerMessages.BROKEN_FLOW,
        eventType: DdEventType.Info,
        context: {
          provider: Provider.PLAYTECH,
          rgsCode: data.gameCode,
          stringifiedPayload: processPayload(data),
        },
      });

      WrapperProxy.resumeIncompleteGame(data.gameCode);
    },
    [gameStudio],
  );

  // this is the actual game switch event received from the provider equivalent to GAME_SWITCH events from other providers
  const handleGameChange: UcipHandler<UcipSwitchGameInfo> = useCallback(
    ({ data }) => {
      if (!data?.gameCode || !isPlaytechLiveGameStudio(gameStudio)) {
        return;
      }
      Logger.rumAction({
        name: ProviderLoggerActionNames.GAME_SWITCH,
        useCase: DdEventCategory.ProviderIntegration,
        description: ProviderLoggerMessages.GAME_SWITCH,
        eventType: DdEventType.Info,
        context: {
          provider: Provider.PLAYTECH,
          rgsCode: data.gameCode,
          stringifiedPayload: processPayload(data),
        },
      });

      WrapperProxy.setGameSwitch(
        {
          rgsCode: data.gameCode as string,
          provider: Provider.PLAYTECH,
        },
        gameSwitchEnabled ?? false,
      );
    },
    [gameStudio, gameSwitchEnabled],
  );

  const handleError = useCallback(
    (payload) => {
      const correlationId = payload?.data?.correlationId;
      const errorCode = payload?.data?.error?.type;
      const errorMessage = payload?.data?.error?.displayMessage;
      const errorType = getErrorType(errorCode, provider, false, payload);

      WrapperProxy.setProviderError({
        display: true,
        errorCode,
        errorMessage,
        errorType,
      });

      dispatch(setErrorCorrelationId(correlationId));
    },
    [dispatch, provider],
  );

  const handleNotImplemented: UcipHandler<null> = useCallback(
    () =>
      Logger.rumAction({
        name: ProviderLoggerMessages.NOT_IMPLEMENTED,
        useCase: DdEventCategory.ProviderIntegration,
        eventType: DdEventType.Info,
        context: {
          provider,
        },
      }),
    [provider],
  );

  const handleCloseGame: UcipHandler<null> = useCallback(() => {
    if (!isPoker) return;
    WrapperProxy.setGameLauncherClosed();
  }, [isPoker]);

  const handleSendCloseGameRequest = useCallback(() => {
    if (!isPoker) return;
    sendUcipMessage({
      _type: PlaytechSendingEvents.CLOSE_GAME_REQUEST,
    });
  }, [isPoker, sendUcipMessage]);

  return {
    handleIntialization,
    handleGameReady,
    handleGameLoadStarted,
    handleGameLoadProgress,
    handleSound,
    handleError,
    handleNotImplemented,
    handleSpinInProgress,
    handleSwitchGame,
    getGameProviderFrame,
    sendUcipMessage,
    volumeLevel,
    handleGameChange,
    handleCloseGame,
    handleSendCloseGameRequest,
  };
};

export default usePlaytechHandlers;
